
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormEvent, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface GoogleCompanyPromptProps {
  companyName: string;
  setCompanyName: (name: string) => void;
  onSubmit: () => Promise<void>;
  loading: boolean;
}

export const GoogleCompanyPrompt = ({
  companyName,
  setCompanyName,
  onSubmit,
  loading
}: GoogleCompanyPromptProps) => {
  const [localLoading, setLocalLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!companyName.trim()) {
      return; // Let the onSubmit function handle the validation
    }
    
    try {
      setLocalLoading(true);
      await onSubmit();
    } catch (error) {
      console.error("Error submitting company name:", error);
    } finally {
      setLocalLoading(false);
    }
  };
  
  // Use either the prop loading or local loading state
  const isLoading = loading || localLoading;

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (error) {
      console.error("Error logging out:", error);
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  return (
    <div className="w-full max-w-md space-y-8 p-8">
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="text-muted-foreground hover:text-foreground"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Log Out
        </Button>
      </div>
      
      <div className="text-center">
        <h2 className="text-2xl font-bold">One more step</h2>
        <p className="text-muted-foreground mt-2">
          Please enter your company name to complete your registration.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="companyName">Company Name</Label>
          <Input
            id="companyName"
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
            placeholder="Enter your company name"
          />
        </div>

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isLoading}
        >
          {isLoading ? "Saving..." : "Continue"}
        </Button>
      </form>
    </div>
  );
};
